// Router próprio (History API). ~4 rotas. Chama unmount() da view atual antes de
// montar a próxima (§8.3). Guard de auth em /app/*. Token de navegação evita
// corrida se o usuário navegar durante o carregamento de uma view.
import { $user, checkSession } from './stores/auth.js';

export interface RouteParams {
  [key: string]: string;
}

/** mount recebe o container e os params; retorna a função de unmount. */
export type ViewMount = (
  container: HTMLElement,
  params: RouteParams,
) => void | (() => void) | Promise<void | (() => void)>;

interface Route {
  pattern: RegExp;
  keys: string[];
  mount: () => Promise<{ default?: ViewMount } | ViewMount>;
  auth: boolean;
}

const routes: Route[] = [];

function compile(path: string): { pattern: RegExp; keys: string[] } {
  const keys: string[] = [];
  const pattern = path.replace(/:[^/]+/g, (m) => {
    keys.push(m.slice(1));
    return '([^/]+)';
  });
  return { pattern: new RegExp(`^${pattern}/?$`), keys };
}

function add(path: string, mount: Route['mount'], auth: boolean): void {
  const { pattern, keys } = compile(path);
  routes.push({ pattern, keys, mount, auth });
}

let container: HTMLElement;
let currentUnmount: (() => void) | null = null;
let navToken = 0;

export function navigate(path: string, replace = false): void {
  if (replace) history.replaceState({}, '', path);
  else history.pushState({}, '', path);
  void render();
}

function resolve(path: string): { route: Route; params: RouteParams } | null {
  for (const route of routes) {
    const m = route.pattern.exec(path);
    if (m) {
      const params: RouteParams = {};
      route.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1] ?? '')));
      return { route, params };
    }
  }
  return null;
}

async function render(): Promise<void> {
  const token = ++navToken;
  const path = location.pathname;
  const match = resolve(path);

  if (!match) {
    navigate('/app', true);
    return;
  }

  // Guard de auth: garante sessão antes de montar rotas protegidas.
  if (match.route.auth && !$user.get()) {
    const user = await checkSession();
    if (token !== navToken) return; // navegou durante a checagem
    if (!user) {
      const next = encodeURIComponent(path + location.search);
      navigate(`/login?next=${next}`, true);
      return;
    }
  }

  const mod = await match.route.mount();
  if (token !== navToken) return; // navegou durante o import

  // Desmonta a view anterior só agora (evita tela em branco em navegação cancelada).
  // O unmount de cada view limpa o container via render(html``, container); NÃO
  // usar container.replaceChildren() aqui — isso ejetaria os marcadores internos
  // do lit-html e quebraria o próximo render (§8.2).
  if (currentUnmount) {
    currentUnmount();
    currentUnmount = null;
  }

  // A cada navegação SPA, checa se há nova versão do app (auto-update do PWA).
  window.__swUpdate?.checkOnNav?.();

  const mountFn: ViewMount = typeof mod === 'function' ? mod : (mod.default as ViewMount);
  const result = await mountFn(container, match.params);
  if (token !== navToken) {
    // Navegou enquanto a view montava — desmonta imediatamente.
    if (typeof result === 'function') result();
    return;
  }
  currentUnmount = typeof result === 'function' ? result : null;
}

function onDocumentClick(e: MouseEvent): void {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const anchor = (e.target as HTMLElement).closest('a[data-link]') as HTMLAnchorElement | null;
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('http')) return;
  e.preventDefault();
  navigate(href);
}

export function startRouter(mount: HTMLElement): void {
  container = mount;
  add('/login', () => import('./views/login/login.view.js'), false);
  add('/app', () => import('./views/dashboard/dashboard.view.js'), true);
  add('/app/teams', () => import('./views/teams/teams.view.js'), true);
  add('/app/blocks', () => import('./views/blocks/blocks.view.js'), true);
  add('/app/split', () => import('./views/split/split.view.js'), true);
  add('/app/users', () => import('./views/users/users.view.js'), true);
  add('/app/m/:uuid', () => import('./views/editor/editor.view.js'), true);
  add('/s/:token', () => import('./views/shared/shared.view.js'), false);

  document.addEventListener('click', onDocumentClick);
  window.addEventListener('popstate', () => void render());
  void render();
}
